<template>
  <div>
    <!-- label is rendered by BaseInput; this is the button that opens the list -->
    <div class="fake-input" @click="openDialog">
      <span class="input-value">{{ displayValue }}</span>
    </div>

    <!-- item list -->
    <el-dialog
      v-model="listDialogVisible"
      :width="'100%'"
      :fullscreen="true"
      :destroy-on-close="true"
      :lock-scroll="true"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <template #title>
        <div class="flex flex-1 justify-center items-center">
          <div class="label-round" style="display: block;">{{ label || $t('list_items') }}</div>
        </div>
      </template>

      <div class="edit-view">
        <div class="overflow-y-auto border-2 border-solid border-gray-300 rounded-lg p-4 list-container">
          <div v-if="localItems.length > 0">
            <div
              v-for="(item, index) in localItems"
              :key="index"
              class="object-item"
              :class="{ 'is-dragging': dragIndex === index }"
              :data-index="index"
              @click="editItem(index)"
            >
              <div class="object-item-head">
                <!-- <i
                  class="el-icon-rank drag-handle"
                  @click.stop
                  @pointerdown="startDrag(index, $event)"
                  @pointermove="onDragMove"
                  @pointerup="endDrag"
                  @pointercancel="endDrag"
                ></i> -->
                <span class="item-index">#{{ index + 1 }}</span>
                <el-button type="danger" size="small" class="delete-btn" @click.stop="removeItem(index)">
                  {{ $t('delete') }}
                </el-button>
              </div>
              <div class="object-item-body">
                <div v-for="cell in summaryOf(item)" :key="cell.code" class="object-cell">
                  <span class="object-cell-label">{{ cell.label }}</span>
                  <span class="object-cell-value">{{ cell.value }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            {{ $t('no_items_to_display') }}
          </div>
        </div>

        <div class="add-section">
          <el-button type="primary" size="large" class="add-btn" :disabled="isAtMax" @click="addItem">
            {{ $t('add') }}
          </el-button>
          <div v-if="isAtMax" class="limit-msg">
            {{ $t('maximum_items_required', { maxLength: maxLength }) }}
          </div>
          <div v-else-if="validationError" class="limit-msg">{{ validationError }}</div>
        </div>

        <div class="dialog-actions">
          <el-button class="cancel-btn" @click="cancelChanges">{{ $t('cancel') }}</el-button>
          <el-button type="primary" class="confirm-btn" :disabled="!!validationError" @click="confirmChanges">
            {{ $t('done') }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- single item editor -->
    <el-dialog
      v-model="editorDialogVisible"
      :width="'100%'"
      :fullscreen="true"
      :destroy-on-close="true"
      :lock-scroll="true"
      :close-on-click-modal="false"
      :before-close="onEditorClose"
      :append-to-body="true"
    >
      <template #title>
        <div class="flex flex-1 justify-center items-center">
          <!-- re-keyed per item so the index change replays the pop -->
          <div :key="editorKey" class="label-round title-pop" style="display: block;">{{ editorTitle }}</div>
        </div>
      </template>

      <div class="edit-view" v-loading.fullscreen.lock="isSwitching">
        <!-- nested FormBuilder: reuses mandatory / min / max / email / phone validation -->
        <FormBuilder ref="itemForm" :formSchema="itemFormSchema" :formModel="itemModel" :isHideSubmitButton="true">
          <template #button-section>
            <div class="dialog-actions">
              <el-button class="cancel-btn" :disabled="isAtMax || isUploading || isSwitching" @click="onNext">
                {{ $t('continue_input') }}
              </el-button>
              <el-button type="primary" class="confirm-btn" :disabled="isUploading || isSwitching" @click="onOk">
                {{ $t('ok') }}
              </el-button>
            </div>
          </template>
        </FormBuilder>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import FormBuilder from '@/components/FormBuilder.vue'
import mitt from '@/utils/mitt'
import { INPUT_COMPONENTS } from '@/utils/constants'
import { openMessage } from '@/utils/helpers'
import { format } from 'date-fns'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { hasActiveAutoIncrement, isAutoIncrementPart } from 'smartbarcode-web-core/src/bo/composeField'
import { parseExternalId, TParsableField } from 'smartbarcode-web-core/src/bo/externalId'
import { eligibleFields } from 'smartbarcode-web-core/src/bo/fieldEligibility'
import { formatCustomFieldValue } from 'smartbarcode-web-core/src/utils/barcodeHelper'
import { DATE_TIME } from 'smartbarcode-web-core/src/utils/constants'
import { ECustomFieldType } from 'smartbarcode-web-core/src/utils/enums/index'
import {
  ICustomFieldData,
  IFile,
  IFormInputSchema,
  IListObjectItem,
  IProjectCustomField,
  TFormSchema,
  TOptions,
} from 'smartbarcode-web-core/src/utils/types/index'
import { Options, Vue } from 'vue-class-component'
import { Emit, Prop, Watch } from 'vue-property-decorator'

@Options({
  components: { FormBuilder },
  name: 'InputListObject',
  emits: ['update:modelValue', 'change'],
})
export default class InputListObject extends Vue {
  @Prop({ type: Array, default: () => [] }) readonly modelValue?: IListObjectItem[]
  @Prop({ type: Object, default: () => ({}) }) readonly subFields?: Record<string, IProjectCustomField>
  @Prop({ type: Boolean, default: false }) readonly isCopyPreviousValue?: boolean
  @Prop({ type: Boolean, default: false }) readonly isComposerEnabled?: boolean
  @Prop({ type: Number }) readonly minLength?: number
  @Prop({ type: Number }) readonly maxLength?: number
  @Prop({ type: String }) readonly label?: string

  listDialogVisible = false
  editorDialogVisible = false
  editingIndex = -1

  // draft buffer, committed to the parent only on Confirm
  localItems: IListObjectItem[] = []
  // flat form model for the item currently being edited, plus a snapshot taken
  // when the editor opened so closing only warns on an actual change
  itemModel: Record<string, unknown> = {}
  itemModelSnapshot: Record<string, unknown> = {}
  // the last value each split source sub-field was parsed from, so the same one is
  // not re-split (mirrors CustomFieldsMixin.lastParsedSources)
  lastParsedSubSources: Record<string, string> = {}
  // reorder state; didDrag swallows the click that follows a drag
  dragIndex = -1
  didDrag = false
  // upload state per nested FILES sub-field, broadcast by InputImageUpload
  uploadingFields: Record<string, boolean> = {}
  isSwitching = false
  editorKey = 0

  created() {
    mitt.on('update:uploadingStatus', this.onUploadingStatus)
  }

  beforeUnmount() {
    mitt.off('update:uploadingStatus', this.onUploadingStatus)
  }

  // mitt types its handler payload as optional
  onUploadingStatus(event?: Record<string, boolean>) {
    if (!event) return
    this.uploadingFields = { ...this.uploadingFields, ...event }
  }

  // an item must not be committed while one of its files is still going up
  get isUploading(): boolean {
    return Object.values(this.uploadingFields).some(Boolean)
  }

  get listValue(): IListObjectItem[] {
    return this.modelValue ?? []
  }

  // the running number tells the user which item they are on, which is the only
  // feedback "Next" gives when it carries the previous values over
  get editorTitle(): string {
    const isEdit = this.editingIndex >= 0
    const no = isEdit ? this.editingIndex + 1 : this.localItems.length + 1
    return `${isEdit ? this.$t('edit_item') : this.$t('add_item')} #${no}`
  }

  get displayValue(): string {
    const action = this.listValue.length === 0 ? this.$t('add') : this.$t('view')
    return `${action}（${this.$t('total_items_count', { count: this.listValue.length })}）`
  }

  // sub-field codes in the order the portal admin defined them
  get orderedKeys(): string[] {
    const subFields = this.subFields ?? {}
    return Object.keys(subFields).sort((a, b) => (subFields[a]?.order ?? 999) - (subFields[b]?.order ?? 999))
  }

  // the sub-schema, shaped for FormBuilder (mirrors CustomFieldsMixin.selectedFormSchema)
  get itemFormSchema(): TFormSchema {
    const subFields = this.subFields ?? {}
    const schema: TFormSchema = {}
    this.orderedKeys.forEach((code) => {
      const { fieldType, multiLine, maxLength, minLength, countryCode, ...others } = subFields[code] ?? {}
      schema[code] = ({
        fieldType,
        ...(fieldType && !!INPUT_COMPONENTS[fieldType] && { component: INPUT_COMPONENTS[fieldType] }),
        ...(maxLength && { maxLength }),
        ...(minLength && { minLength }),
        ...(countryCode && { countryCode }),
        ...(multiLine && { multiLine }),
        ...(fieldType === ECustomFieldType.ESIGN && { disableError: true }),
        ...others,
        ...(subFields[code]?.isReadOnly && { disabled: true }),
        // IProjectCustomField.default is string | number, IFormInputSchema.default is string
      } as unknown) as IFormInputSchema

      // Auto-increment never changes a field's own behaviour: input, isMandatory and
      // validation stay exactly as configured; the server appends the number at submit.
      // The one exception: a composer made of auto rows only has no input source, so
      // with every row disabled nothing can ever give the field a value — drop it.
      // isComposerEnabled gates it: the edit screen shows the stored composed value as-is.
      if (fieldType === ECustomFieldType.TEXT && this.isComposerEnabled) {
        const composer = schema[code].composer
        const parts = composer?.parts ?? []
        if (parts.length > 0 && parts.every(isAutoIncrementPart) && !hasActiveAutoIncrement(composer)) {
          delete schema[code]
        }
      }
    })
    return schema
  }

  get isAtMax(): boolean {
    return this.maxLength !== undefined && this.localItems.length >= Number(this.maxLength)
  }

  get validationError(): string {
    if (this.minLength && this.localItems.length < Number(this.minLength)) {
      return this.$t('minimum_items_required', { minLength: this.minLength })
    }
    if (this.maxLength !== undefined && this.localItems.length > Number(this.maxLength)) {
      return this.$t('maximum_items_required', { maxLength: this.maxLength })
    }
    return ''
  }

  summaryOf(item: IListObjectItem) {
    const subFields = this.subFields ?? {}
    return this.orderedKeys.map((code) => ({
      code,
      label: subFields[code]?.label || code,
      value: formatCustomFieldValue(item?.customFields?.[code]),
    }))
  }

  openDialog() {
    this.localItems = cloneDeep(this.listValue)
    this.listDialogVisible = true
    // no value yet -> go straight into the first item, as the portal schema intends
    if (this.localItems.length === 0) this.addItem()
  }

  addItem() {
    if (this.isAtMax) {
      openMessage(this.$t('maximum_items_reached', { maxItems: this.maxLength }), 'warning')
      return
    }
    this.editingIndex = -1
    this.openEditor(this.blankModel())
  }

  editItem(index: number) {
    if (this.didDrag) {
      this.didDrag = false
      return
    }
    this.editingIndex = index
    this.openEditor(this.toFormModel(this.localItems[index]))
  }

  openEditor(model: Record<string, unknown>) {
    this.itemModel = model
    this.itemModelSnapshot = cloneDeep(model)
    // a new element must not inherit the previous element's parse memory — but an
    // EXISTING element's sources are seeded as already-parsed, otherwise assigning
    // itemModel above fires the deep watcher against an empty cache and re-splits
    // over targets the user may have corrected by hand (WR-01; same reason level-1
    // keeps lastParsedSources).
    this.lastParsedSubSources = {}
    if (this.editingIndex >= 0) {
      this.orderedKeys
        .filter((code) => this.subFields?.[code]?.parsers?.length && typeof model[code] === 'string' && model[code] !== '')
        .forEach((code) => (this.lastParsedSubSources[code] = model[code] as string))
    }
    this.editorKey++
    this.editorDialogVisible = true
  }

  // pointer events instead of HTML5 drag-and-drop: the latter is dead on touch
  startDrag(index: number, e: PointerEvent) {
    this.dragIndex = index
    this.didDrag = false
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }

  // ponytail: swaps when the pointer crosses another row, no auto-scroll at the
  // list edges. Good enough for the handful of items a sub-schema holds.
  onDragMove(e: PointerEvent) {
    if (this.dragIndex < 0) return
    const over = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement)?.closest('.object-item')
    const to = Number((over as HTMLElement)?.dataset?.index ?? -1)
    if (!over || to < 0 || to === this.dragIndex) return
    this.localItems.splice(to, 0, ...this.localItems.splice(this.dragIndex, 1))
    this.dragIndex = to
    this.didDrag = true
  }

  endDrag() {
    this.dragIndex = -1
  }

  async removeItem(index: number) {
    try {
      await this.$confirm(this.$t('confirm_delete_item'), this.$t('info'), {
        confirmButtonText: this.$t('delete'),
        cancelButtonText: this.$t('cancel'),
        type: 'warning',
        confirmButtonClass: 'el-button--danger',
      })
    } catch (e) {
      return
    }
    this.localItems.splice(index, 1)
  }

  // The header close abandons the item being entered, so a half-filled form can
  // be dropped without OK's validation blocking the way back to the list.
  async onEditorClose(hide: (cancel?: boolean) => void) {
    if (!isEqual(this.itemModel, this.itemModelSnapshot)) {
      try {
        await this.$confirm(this.$t('unsaved_changes', { changes: '' }), this.$t('title_unsaved_changes'), {
          confirmButtonText: this.$t('discard_changes'),
          cancelButtonText: this.$t('keep_editing'),
          type: 'warning',
          confirmButtonClass: 'el-button--danger',
        })
      } catch (e) {
        hide(true)
        return
      }
    }
    hide()
  }

  onOk() {
    if (!this.commitItem()) return
    this.editorDialogVisible = false
  }

  onNext() {
    const previous = this.itemModel
    if (!this.commitItem()) return
    if (this.isAtMax) {
      openMessage(this.$t('maximum_items_reached', { maxItems: this.maxLength }), 'warning')
      this.editorDialogVisible = false
      return
    }
    // "Next" carries the previous item's values over when the portal enables it.
    // A short beat before the swap, otherwise the form looks like nothing happened.
    this.editingIndex = -1
    this.isSwitching = true
    setTimeout(() => {
      this.openEditor(this.isCopyPreviousValue ? cloneDeep(previous) : this.blankModel())
      this.isSwitching = false
    }, 300)
  }

  commitItem(): boolean {
    if (this.isUploading) {
      openMessage(this.$t('uploading_in_progress'), 'warning')
      return false
    }
    const form = this.$refs.itemForm as { validateAll?: () => boolean } | undefined
    if (form?.validateAll && !form.validateAll()) return false

    // CR-01: localItems always hold RAW values — composing here and writing the result
    // back into the element's storage made a re-edit compose again on top of the already
    // composed value ("x-y" → "x-y-y"). Non-auto sub-field composers are applied exactly
    // once, at request-build time, in CustomFieldsMixin.composeListObjectRows.
    const customFields = this.fromFormModel()
    if (this.editingIndex >= 0) {
      const existing = this.localItems[this.editingIndex]
      this.localItems.splice(this.editingIndex, 1, {
        createdDateTime: existing?.createdDateTime || new Date().toISOString(),
        customFields,
      })
    } else {
      this.localItems.push({ createdDateTime: new Date().toISOString(), customFields })
    }
    return true
  }

  // Split: a sub-field carrying parsers writes into the sibling sub-fields it
  // targets as the user types (mirrors CustomFieldsMixin.applyFieldParsers).
  @Watch('itemModel', { deep: true })
  applySubFieldParsers() {
    if (!this.isComposerEnabled) return // edit screen must not re-split (D-08-08)

    const subFields = this.subFields ?? {}
    this.orderedKeys
      .filter((code) => subFields[code]?.fieldType === ECustomFieldType.TEXT && subFields[code]?.parsers?.length)
      .forEach((code) => {
        const source = this.itemModel[code]
        if (typeof source !== 'string' || source === '') {
          delete this.lastParsedSubSources[code]
          return
        }
        if (this.lastParsedSubSources[code] === source) return

        const parsers = subFields[code]?.parsers ?? []
        const wanted = parsers.map((parser) => parser.targetFieldPath)
        const targets = eligibleFields(
          this.orderedKeys
            .map((targetCode) => ({
              key: `customFields.${targetCode}`,
              label: subFields[targetCode]?.label ?? '',
              type: subFields[targetCode]?.fieldType as ECustomFieldType,
              selections: subFields[targetCode]?.selections ?? {},
            }))
            .filter((target) => wanted.includes(target.key)),
          'split'
        ) as TParsableField[]
        if (!targets.length) return

        // Cached before the parse so a rule that throws is not re-run on every
        // later keystroke.
        this.lastParsedSubSources[code] = source
        try {
          Object.entries(parseExternalId(source, targets, parsers)).forEach(([path, value]) => {
            const targetKey = path.replace(/^customFields\./, '')
            const next = this.coerceParsedSubValue(targetKey, value)
            // Compared by value: a multi-select target holds an array, and a fresh
            // array is never === the previous one, so the watcher would re-fire forever.
            if (next !== undefined && !isEqual(this.itemModel[targetKey], next)) this.itemModel[targetKey] = next
          })
        } catch (error) {
          // A rule that fails to compile throws here. This runs on every keystroke, so
          // one bad setting must not take the form down with it (T-08-24).
        }
      })
  }

  /** A parsed value is always a string; a multi-select target needs an array. */
  coerceParsedSubValue(targetKey: string, value: string | string[]): unknown {
    const fieldType = this.subFields?.[targetKey]?.fieldType
    if (fieldType === ECustomFieldType.MULTI_SELECT) return Array.isArray(value) ? value : [value]
    // ponytail: no DATE branch here (unlike CustomFieldsMixin.coerceParsedValue) —
    // portal's LIST_OBJECT_SUB_FIELD_TYPES only allows TEXT/NUMBER/FILES sub-fields
    // today, so a DATE parse target cannot exist. Add one by copying the mixin's
    // parse/isValid/format block if that list ever grows to include DATE.
    return Array.isArray(value) ? value[0] : value
  }

  confirmChanges() {
    if (this.validationError) {
      openMessage(this.validationError, 'error')
      return
    }
    this.updateModelValue(cloneDeep(this.localItems))
    this.emitChange(cloneDeep(this.localItems))
    this.listDialogVisible = false
  }

  async cancelChanges() {
    if (!isEqual(this.localItems, this.listValue)) {
      try {
        await this.$confirm(this.$t('unsaved_changes', { changes: '' }), this.$t('title_unsaved_changes'), {
          confirmButtonText: this.$t('discard_changes'),
          cancelButtonText: this.$t('keep_editing'),
          type: 'warning',
        })
      } catch (e) {
        return
      }
    }
    this.localItems = cloneDeep(this.listValue)
    this.listDialogVisible = false
  }

  @Emit('update:modelValue')
  updateModelValue(value: IListObjectItem[]) {
    return value
  }

  @Emit('change')
  emitChange(value: IListObjectItem[]) {
    return value
  }

  blankModel(): Record<string, unknown> {
    const subFields = this.subFields ?? {}
    const model: Record<string, unknown> = {}
    this.orderedKeys.forEach((code) => {
      const fieldType = subFields[code]?.fieldType
      if (fieldType === ECustomFieldType.MULTI_SELECT || fieldType === ECustomFieldType.FILES) {
        model[code] = []
      } else {
        model[code] = subFields[code]?.default ?? ''
      }
    })
    return model
  }

  // customFields -> flat form model (mirrors CustomFieldsMixin.convertProcessedDataToOriginal)
  toFormModel(item?: IListObjectItem): Record<string, unknown> {
    const model = this.blankModel()
    const customFields = item?.customFields ?? {}
    Object.keys(customFields).forEach((code) => {
      const data = customFields[code]
      if (!data) return
      switch (data.fieldType) {
        case ECustomFieldType.SINGLE_SELECT:
          model[code] = data.singleSelect?.key
          break
        case ECustomFieldType.MULTI_SELECT:
          model[code] = (data.multiSelect ?? []).map((r) => r.key)
          break
        case ECustomFieldType.FILES:
          model[code] = data.files ?? []
          break
        case ECustomFieldType.PHONE_NUMBER:
          model[code] = data.phoneNumber?.number
          break
        default:
          model[code] = ((data as unknown) as Record<string, unknown>)[data.fieldType as string]
      }
    })
    return model
  }

  // flat form model -> customFields (mirrors CustomFieldsMixin.generateCustomDataForRequest,
  // restricted to the simple types allowed inside a sub-schema)
  fromFormModel(): Record<string, ICustomFieldData> {
    const subFields = this.subFields ?? {}
    const out: Record<string, ICustomFieldData> = {}
    this.orderedKeys.forEach((code) => {
      const fieldType = subFields[code]?.fieldType
      if (!fieldType) return
      let raw = this.itemModel[code]
      if (raw instanceof Date) raw = format(raw, DATE_TIME.WITHOUT_SECOND)
      if (raw === undefined || raw === null || raw === '') return
      if (Array.isArray(raw) && raw.length === 0) return

      switch (fieldType) {
        case ECustomFieldType.TEXT:
          out[code] = { fieldType, text: raw as string }
          break
        case ECustomFieldType.NUMBER:
          out[code] = { fieldType, number: Number(raw) }
          break
        case ECustomFieldType.EMAIL:
          out[code] = { fieldType, email: raw as string }
          break
        case ECustomFieldType.ESIGN:
          out[code] = { fieldType, eSign: raw as string }
          break
        case ECustomFieldType.DATE:
          out[code] = { fieldType, date: raw as string }
          break
        case ECustomFieldType.DATE_TIME_DATA:
          out[code] = { fieldType, dateTimeData: raw as string }
          break
        case ECustomFieldType.PHONE_NUMBER:
          out[code] = {
            fieldType,
            phoneNumber: {
              countryCode: subFields[code]?.countryCode ?? '',
              number: String(raw).replaceAll('-', ''),
            },
          }
          break
        case ECustomFieldType.SINGLE_SELECT: {
          const selections = (subFields[code]?.selections ?? {}) as TOptions
          out[code] = {
            fieldType,
            singleSelect: { key: raw as string, value: selections[raw as string]?.label ?? '' },
          }
          break
        }
        case ECustomFieldType.MULTI_SELECT: {
          const selections = (subFields[code]?.selections ?? {}) as TOptions
          out[code] = {
            fieldType,
            multiSelect: (raw as string[])
              .filter((k) => k !== '')
              .map((k) => ({ key: k, value: selections[k]?.label ?? '' })),
          }
          break
        }
        case ECustomFieldType.FILES:
          out[code] = {
            fieldType,
            files: (raw as unknown[]).map((v) => (typeof v === 'string' ? { path: v } : (v as IFile))),
          }
          break
      }
    })
    return out
  }
}
</script>

<style lang="scss" scoped>
.fake-input {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid $dark-grey;
  border-radius: 6px;
  background-color: $dark-grey;
  min-height: 44px;
  box-sizing: border-box;
  color: white;
}

.input-value {
  flex: 1;
  color: white;
  font-size: 14px;
  text-align: center;
}

.list-container {
  height: 40vh;
  max-width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.object-item {
  padding: 8px 12px;
  margin-bottom: 8px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  cursor: pointer;
}

.object-item.is-dragging {
  opacity: 0.6;
  border-color: #909399;
}

.object-item-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.drag-handle {
  flex: 0 0 auto;
  padding: 4px;
  font-size: 18px;
  color: #909399;
  cursor: grab;
  touch-action: none;
}

.item-index {
  flex: 1 1 auto;
  font-weight: 600;
}

.delete-btn {
  flex: 0 0 auto;
  margin: 0;
}

.object-cell {
  display: flex;
  gap: 8px;
  line-height: 1.5;
  word-break: break-all;
}

.object-cell-label {
  flex: 0 0 40%;
  max-width: 40%;
  color: #888;
}

.object-cell-value {
  flex: 1 1 auto;
}

.empty-state {
  text-align: center;
  color: #888;
  padding: 24px 0;
}

.add-section {
  margin-top: 16px;

  .add-btn {
    width: 100%;
    height: 44px;
  }
}

.limit-msg {
  margin-top: 8px;
  color: #f56c6c;
  font-size: 12px;
  text-align: center;
}

// InputNumber hard-codes 230px, which plus the two 40px reader icons exactly
// fills this dialog's row, so justify-evenly has no space left to distribute.
// Let the input take what is left instead, and pin the icons so they neither
// shrink nor break between the two svgs.
.edit-view :deep(.el-input-number) {
  flex: 1 1 auto;
  width: auto;
  min-width: 0;
  margin-right: 8px;
}

.edit-view :deep(.el-input-number + span) {
  flex: 0 0 auto;
  white-space: nowrap;
}

.title-pop {
  animation: title-pop 900ms ease-out;
}

@keyframes title-pop {
  0% {
    transform: scale(0.85);
    opacity: 0.3;
  }
  60% {
    transform: scale(1.08);
    opacity: 1;
  }
  100% {
    transform: scale(1);
  }
}

.dialog-actions {
  // FormBuilder wraps #button-section in a bare `.flex`, so this has to grow or
  // the buttons and the divider shrink to their content width
  flex: 1;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;

  .cancel-btn,
  .confirm-btn {
    flex: 1;
    height: 44px;
    font-weight: 500;
  }

  .cancel-btn {
    background-color: white;
    border-color: #6c757d;
    color: #6c757d;
  }
}
</style>
